import React, { useEffect, useState } from 'react'
import { RepReportDataSource } from '../../services/apis/RepReportDataSource'
import Loader from '../../shared/Loader/Loader'

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
}

const FALLBACK_FILENAME = 'assessment-anonymous-answer.zip'

// ข้อความ error เริ่มต้น (ใช้เมื่อไม่รู้จัก code หรือ parse error ไม่ได้)
const DEFAULT_ERROR_MESSAGE = 'ไม่สามารถดาวน์โหลดไฟล์ได้ กรุณาลองใหม่อีกครั้ง'

// แปล error code จาก API เป็นข้อความไทย — API ส่งรูปแบบ { status: { code, description } }
const ERROR_MESSAGE_BY_CODE = {
  4001: 'ลิงก์หมดอายุหรือไม่ถูกต้อง กรุณาขอลิงก์ดาวน์โหลดใหม่อีกครั้ง',
}

// อ่านชื่อไฟล์จาก header content-disposition (รองรับทั้ง filename="x" และ filename*=UTF-8''x)
const getFilenameFromHeaders = (headers, fallback) => {
  const disposition = headers && headers['content-disposition']
  if (!disposition) return fallback

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch (e) {
      return utf8Match[1]
    }
  }

  const match = /filename="?([^";]+)"?/i.exec(disposition)
  return match ? match[1] : fallback
}

const triggerBrowserDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// error body จะมาเป็น Blob เพราะ responseType: 'blob' ต้องแปลงกลับเป็น text เพื่ออ่าน code จริง
// รูปแบบ error จาก API: { status: { code: "4001", description: "Link expired or invalid" }, data: null }
// แปล code เป็นข้อความไทยเอง (ไม่ใช้ description ที่เป็นอังกฤษจาก API)
const resolveErrorMessage = async (responseData) => {
  try {
    if (responseData instanceof Blob) {
      const text = await responseData.text()
      const json = JSON.parse(text)
      const code = json && json.status && json.status.code
      if (code != null && ERROR_MESSAGE_BY_CODE[code]) {
        return ERROR_MESSAGE_BY_CODE[code]
      }
    }
  } catch (e) {
    // ignore parse error, ใช้ข้อความ default
  }
  return DEFAULT_ERROR_MESSAGE
}

const DownloadCsvPage = () => {
  const [status, setStatus] = useState(STATUS.LOADING)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    download()
  }, [])

  const download = async () => {
    const token = new URLSearchParams(window.location.search).get('token')

    if (!token) {
      setStatus(STATUS.ERROR)
      setErrorMessage('ไม่พบ token ในลิงก์ดาวน์โหลด')
      return
    }

    try {
      const response = await RepReportDataSource.downloadAssessmentAnonymousAnswerCsv(token)

      // interceptor ตั้ง validateStatus: () => true และ 403 จะ resolve เข้ามาที่นี่ (ไม่เข้า catch)
      // responseType เป็น 'blob' ทำให้ทั้งไฟล์จริงและ error JSON มาเป็น Blob เหมือนกัน
      // จึงต้องเช็ค 2 ชั้น: (1) http status 2xx (2) content-type ไม่ใช่ json
      const isHttpOk = response.status >= 200 && response.status < 300
      const contentType = (response.headers && response.headers['content-type']) || ''
      const isFile = isHttpOk && !contentType.includes('application/json')

      if (!isFile) {
        const message = await resolveErrorMessage(response.data)
        setStatus(STATUS.ERROR)
        setErrorMessage(message)
        return
      }

      const filename = getFilenameFromHeaders(response.headers, FALLBACK_FILENAME)
      triggerBrowserDownload(response.data, filename)
      setStatus(STATUS.SUCCESS)
    } catch (error) {
      // กรณีที่ interceptor reject จริง (เช่น 400/404) หรือ network error
      const message = await resolveErrorMessage(error && error.data)
      setStatus(STATUS.ERROR)
      setErrorMessage(message)
    }
  }

  if (status === STATUS.LOADING) {
    return <Loader />
  }

  return (
    <div style={{ textAlign: 'center', padding: '64px 16px' }}>
      {status === STATUS.SUCCESS ? (
        <>
          <h4>ดาวน์โหลดไฟล์เรียบร้อยแล้ว</h4>
          <p>คุณสามารถปิดหน้าต่างนี้ได้เลย</p>
          <p>*หากการดาวน์โหลดไม่เริ่มโดยอัตโนมัติ กรุณาเปิดลิงก์จากอีเมลอีกครั้ง</p>
        </>
      ) : (
        <>
          <h4>ไม่สามารถดาวน์โหลดไฟล์ได้</h4>
          <p>{errorMessage}</p>
        </>
      )}
    </div>
  )
}

export default DownloadCsvPage
